// components/Outreach/DialogsTab.js — добавлена визуализация contact_value для CONTACT_FORM
import React, { useState, useEffect, useRef, useCallback } from "react";
import { getCookie } from "../../services/cookie";
import { HiSearch, HiX, HiCheck, HiPlus } from "react-icons/hi";

const API_BASE = "https://flautreach.com/flautreach/api/outreach";
const PAGE_SIZE = 30;

const channelIcon = (ch) => {
  if (!ch) return "";
  if (ch === "EMAIL" || ch === "Email") return "📧";
  if (ch === "FB_MESSENGER" || ch === "FB Messenger") return "💬";
  if (ch === "CONTACT_FORM" || ch === "Contact Form") return "📝";
  return "📨";
};
const channelColor = (ch) => {
  if (!ch) return {};
  if (ch === "EMAIL" || ch === "Email") return { bg: "#dbeafe", color: "#1e40af" };
  if (ch === "FB_MESSENGER" || ch === "FB Messenger") return { bg: "#dcfce7", color: "#166534" };
  if (ch === "CONTACT_FORM" || ch === "Contact Form") return { bg: "#fef3c7", color: "#92400e" };
  return { bg: "#f3f4f6", color: "#374151" };
};
const formatMessageText = (text) => {
  if (!text) return "";
  try {
    const parsed = JSON.parse(text);
    if (parsed.subject && parsed.body) return `📌 ${parsed.subject}\n\n${parsed.body}`;
    if (parsed.body) return parsed.body;
    return text;
  } catch { return text; }
};

// ─── Карточка диалога ──────────────────────────────────────────
function DialogCard({ dialog, isSelected, onClick }) {
  const events = dialog.events || [];
  const hasPending = dialog.has_pending || false;
  const incomingCnt = events.filter(e => e.event_type === "incoming").length;
  const hasOurReply = events.some(e => e.event_type === "outgoing_reply");
  const lastEvent = events.length > 0 ? events[events.length - 1] : null;
  const lastText = lastEvent ? formatMessageText(lastEvent.text || "").replace(/\n/g, " ").substring(0, 50) : "";
  const lastTs = lastEvent ? String(lastEvent.event_time || "").substring(0, 16) : "";

  return (
    <div onClick={onClick} style={{
      padding: "10px 12px", borderBottom: "1px solid #e5e7eb",
      background: isSelected ? "#eff6ff" : "#fff",
      borderLeft: isSelected ? "3px solid #2563eb" : "3px solid transparent",
      cursor: "pointer", transition: "background 0.15s",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: hasPending ? "#d97706" : "#1f2937" }}>
          {dialog.username || dialog.target_url?.replace(/https?:\/\//, '').substring(0, 25) || "—"}
        </span>
        <span style={{ color: "#9ca3af", fontSize: 12 }}>#{dialog.execution_id}</span>
        <div style={{ flex: 1 }} />
        {hasPending && <span style={{ background: "#fef3c7", color: "#d97706", borderRadius: 8, fontSize: 11, padding: "2px 8px", fontWeight: 600 }}>⏳</span>}
        {incomingCnt > 0 && <span style={{ background: "#dcfce7", color: "#16a34a", borderRadius: 8, fontSize: 11, padding: "2px 8px" }}>💬{incomingCnt}</span>}
        {hasOurReply && <span style={{ background: "#fefce8", color: "#ca8a04", borderRadius: 8, fontSize: 11, padding: "2px 8px" }}>↩</span>}
      </div>
      {dialog.email && <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 2 }}>✉ {dialog.email}</div>}
      {dialog.website && <div style={{ fontSize: 12, color: "#2563eb", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>🌐 {dialog.website.replace(/https?:\/\//, '')}</div>}
      {dialog.links?.length > 0 && <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 2 }}>🔗 {dialog.links.length} ссылок</div>}
      {dialog.channel && <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 2 }}>{channelIcon(dialog.channel)} {dialog.channel}</div>}
      <div style={{ display: "flex", gap: 6, marginBottom: 2 }}>
        <span style={{ fontSize: 12, color: "#6b7280" }}>{dialog.campaign || "—"}</span>
        <span style={{ fontSize: 12, color: "#d1d5db" }}>·</span>
        <span style={{ fontSize: 12, color: "#6b7280" }}>{dialog.worker || "—"}</span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span style={{ fontSize: 12, color: "#9ca3af", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lastText}</span>
        <span style={{ fontSize: 11, color: "#d1d5db", marginLeft: 8 }}>{lastTs?.replace("T", " ")}</span>
      </div>
    </div>
  );
}

// ─── Пузырь сообщения ──────────────────────────────────────────
function MessageBubble({ event, isChecked, onToggle }) {
  const evType = event.event_type;
  const rawText = event.text || "";
  const text = formatMessageText(rawText).replace(/\\n/g, "\n");
  const ts = String(event.event_time || "").replace("T", " ").substring(0, 16);
  const worker = event.worker || "";
  const channel = event.channel || "";
  const chIcon = channelIcon(channel);
  const chStr = channel ? ` · ${chIcon} ${channel}` : "";

  // Для CONTACT_FORM показываем contact_value
  const showContact = event.contact_type === "CONTACT_FORM" && event.contact_value;

  const config = {
    outgoing:       { bg: "#eff6ff", border: "#bfdbfe", tag: `${worker ? `▶ Отправлено · ${worker}` : "▶ Отправлено"}${chStr}`, tagColor: "#2563eb", tsColor: "#93c5fd", align: "flex-end" },
    incoming:       { bg: "#f0fdf4", border: "#bbf7d0", tag: "◀ Получено", tagColor: "#16a34a", tsColor: "#86efac", align: "flex-start" },
    outgoing_reply: { bg: "#fefce8", border: "#fde68a", tag: worker ? `▶ Ответ · ${worker}` : "▶ Ответ", tagColor: "#ca8a04", tsColor: "#fde68a", align: "flex-end" },
    intention:      { bg: "#faf5ff", border: "#e9d5ff", tag: "⏳ Запланировано", tagColor: "#9333ea", tsColor: "#d8b4fe", align: "center" },
    pending_reply:  { bg: "#faf5ff", border: "#e9d5ff", tag: "⏳ Запланированный ответ", tagColor: "#9333ea", tsColor: "#d8b4fe", align: "center" },
  };

  const c = config[evType] || config.intention;
  const isPendingReply = evType === "pending_reply";

  return (
    <div style={{ display: "flex", justifyContent: c.align, marginBottom: 8, alignItems: "center", gap: 8 }}>
      {isPendingReply && (
        <input type="checkbox" checked={isChecked} onChange={onToggle}
          style={{ width: 18, height: 18, cursor: "pointer", flexShrink: 0 }} />
      )}
      <div style={{
        background: c.bg, border: `1px solid ${c.border}`, borderRadius: 10,
        padding: "10px 14px", maxWidth: isPendingReply ? "calc(85% - 26px)" : "85%", minWidth: 120,
        ...(isPendingReply ? { borderStyle: "dashed", borderWidth: 2 } : {}),
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, gap: 12 }}>
          <div>
            <span style={{ color: c.tagColor, fontSize: 11, fontWeight: 700 }}>{c.tag}</span>
            {showContact && (
              <span style={{ color: "#6b7280", fontSize: 11, marginLeft: 8 }}>
                📧 {event.contact_value}
              </span>
            )}
          </div>
          <span style={{ color: c.tsColor, fontSize: 10 }}>{ts}</span>
        </div>
        {showContact && <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>📧 {event.contact_value}</div>}
        <div style={{ fontSize: 13, whiteSpace: "pre-wrap", wordBreak: "break-word", color: "#1f2937", lineHeight: 1.5 }}>
          {text}
        </div>
      </div>
    </div>
  );
}

// ─── Правая панель с подробной информацией ─────────────────────
function DialogPanel({ dialog, onClose, onAddIntention, onExecuteIntentions }) {
  const scrollRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [checkedIds, setCheckedIds] = useState(new Set());

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      setTimeout(() => { scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 100);
    }
  }, [dialog, checkedIds]);

  if (!dialog) {
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", background: "#f9fafb", ...(isMobile ? { display: "none" } : {}) }}>
        <span style={{ color: "#9ca3af", fontSize: 15 }}>← Выбери диалог</span>
      </div>
    );
  }

  const events = dialog.events || [];
  const hasPending = dialog.has_pending || false;
  const chColors = channelColor(dialog.channel);

  const contactType = dialog.channel || "EMAIL";
  let contactValue = dialog.email || "";
  if (!contactValue && contactType === "FB_MESSENGER") contactValue = dialog.target_url || "";

  const outCount = events.filter(e => e.event_type === "outgoing" || e.event_type === "outgoing_reply").length;
  const incCount = events.filter(e => e.event_type === "incoming").length;
  const penCount = events.filter(e => e.event_type === "intention" || e.event_type === "pending_reply").length;

  const handleCreateIntention = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    await onAddIntention(dialog.execution_id, contactType, contactValue, replyText);
    setSending(false);
    setReplyText("");
  };

  const toggleCheck = (intentionId) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      next.has(intentionId) ? next.delete(intentionId) : next.add(intentionId);
      return next;
    });
  };

  const handleExecute = async () => {
    if (checkedIds.size === 0) return;
    setSending(true);
    await onExecuteIntentions(dialog.execution_id, [...checkedIds]);
    setSending(false);
    setCheckedIds(new Set());
  };

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", background: "#fff", ...(isMobile ? { position: "fixed", inset: 0, zIndex: 1000 } : {}) }}>
      {/* Профиль-бар */}
      <div style={{ background: "#f8fafc", borderBottom: "1px solid #e5e7eb", padding: "12px 16px", display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
        {isMobile && (
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><HiX size={18} /></button>
        )}
        <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#e5e7eb", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, color: "#6b7280", flexShrink: 0 }}>
          {(dialog.username || dialog.target_url || "?")[0].toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#1f2937", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            {hasPending && "⏳"}
            {dialog.username || dialog.target_url?.replace(/https?:\/\//, '').substring(0, 30) || "—"}
            <span style={{ fontSize: 12, color: "#9ca3af", fontWeight: 400 }}>#{dialog.execution_id}</span>
            <span style={{ fontSize: 11, color: "#6b7280" }}>
              {outCount > 0 && `📤${outCount} `}
              {incCount > 0 && `📥${incCount} `}
              {penCount > 0 && `⏳${penCount}`}
            </span>
          </div>
          {dialog.full_name && <div style={{ fontSize: 12, color: "#6b7280" }}>{dialog.full_name}</div>}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 4, alignItems: "center" }}>
            {dialog.email && <span style={{ fontSize: 12, color: "#6b7280" }}>✉ {dialog.email}</span>}
            {dialog.website && (
              <a href={dialog.website} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: "#2563eb", textDecoration: "underline" }}>🌐 Сайт</a>
            )}
            {dialog.links?.length > 0 && <span style={{ fontSize: 12, color: "#2563eb" }}>🔗 {dialog.links.length} ссылок</span>}
            {dialog.followers > 0 && <span style={{ fontSize: 12, color: "#6b7280" }}>👥 {Number(dialog.followers).toLocaleString("ru")}</span>}
            {dialog.business_category && <span style={{ fontSize: 12, color: "#6b7280" }}>{dialog.business_category}</span>}
            {dialog.channel && (
              <span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 8, background: chColors.bg, color: chColors.color, fontWeight: 600 }}>
                {channelIcon(dialog.channel)} {dialog.channel}
              </span>
            )}
            {dialog.candidate_channels?.length > 0 && (
              <span style={{ fontSize: 11, color: "#9ca3af" }}>Доступны: {dialog.candidate_channels.map(ch => channelIcon(ch)).join(" ")}</span>
            )}
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 2 }}>
            <span style={{ fontSize: 12, color: "#6b7280" }}>📢 {dialog.campaign || "—"}</span>
            <span style={{ fontSize: 12, color: "#d1d5db" }}>·</span>
            <span style={{ fontSize: 12, color: "#6b7280" }}>🤖 {dialog.worker || "—"}</span>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          {dialog.username && (
            <a href={`https://instagram.com/${dialog.username}`} target="_blank" rel="noopener noreferrer" style={{ padding: "4px 10px", borderRadius: 6, background: "#2563eb", color: "#fff", textDecoration: "none", fontSize: 11, textAlign: "center" }}>🔗 IG</a>
          )}
          {dialog.target_url && (
            <a href={dialog.target_url} target="_blank" rel="noopener noreferrer" style={{ padding: "4px 10px", borderRadius: 6, background: "#f3f4f6", color: "#374151", textDecoration: "none", fontSize: 11, textAlign: "center" }}>🔗 Цель</a>
          )}
        </div>
      </div>

      {/* Ссылки */}
      {dialog.links?.length > 0 && (
        <div style={{ padding: "8px 16px", background: "#f0f9ff", borderBottom: "1px solid #bae6fd", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: "#0369a1" }}>🔗 Ссылки:</span>
          {dialog.links.slice(0, 8).map((link, i) => (
            <a key={i} href={link.startsWith("http") ? link : "https://" + link} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: "#2563eb", textDecoration: "underline" }}>
              {link.length > 50 ? link.substring(0, 50) + "..." : link.replace(/https?:\/\//, '')}
            </a>
          ))}
          {dialog.links.length > 8 && <span style={{ fontSize: 11, color: "#9ca3af" }}>+{dialog.links.length - 8} ещё</span>}
        </div>
      )}

      {/* Чат */}
      <div ref={scrollRef} style={{ flex: 1, overflow: "auto", padding: "16px", background: "#f9fafb" }}>
        {events.length === 0 ? (
          <div style={{ textAlign: "center", color: "#9ca3af", padding: 40, fontSize: 14 }}>Нет сообщений</div>
        ) : (
          events.map((ev, i) => (
            <MessageBubble key={i} event={ev} isChecked={ev.event_type === "pending_reply" && checkedIds.has(ev.intention_id)} onToggle={() => ev.event_type === "pending_reply" && toggleCheck(ev.intention_id)} />
          ))
        )}
      </div>

      {/* Панель ввода */}
      <div style={{ borderTop: "1px solid #e5e7eb", padding: "12px 16px", background: "#fff" }}>
        <div style={{ display: "flex", gap: 8 }}>
          <textarea placeholder="Напишите ответ..." value={replyText} onChange={e => setReplyText(e.target.value)} rows={2} style={{ flex: 1, padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 13, resize: "vertical" }} />
          <button onClick={handleCreateIntention} disabled={sending || !replyText.trim()} style={{ padding: "0 16px", background: "#d97706", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13, whiteSpace: "nowrap" }}>
            <HiPlus size={16} style={{ marginRight: 4 }} /> Создать
          </button>
        </div>
        {checkedIds.size > 0 && (
          <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end" }}>
            <button onClick={handleExecute} disabled={sending} style={{ padding: "6px 16px", background: sending ? "#9ca3af" : "#16a34a", color: "#fff", border: "none", borderRadius: 6, cursor: sending ? "not-allowed" : "pointer", fontWeight: 600, fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
              <HiCheck size={16} /> Выполнить отмеченные ({checkedIds.size})
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Основной компонент ─────────────────────────────────────────
export function DialogsTab({ add }) {
  const token = () => getCookie("authToken");
  const getHeaders = () => {
    const t = token();
    return { "Content-Type": "application/json", ...(t ? { Authorization: `Bearer ${t}` } : {}) };
  };

  const [allDialogs, setAllDialogs] = useState([]);
  const [selectedDialog, setSelectedDialog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [searchQuery, setSearchQuery] = useState("");
  const [campaignFilter, setCampaignFilter] = useState("");
  const [workerFilter, setWorkerFilter] = useState("");
  const [repliesOnly, setRepliesOnly] = useState(false);
  const [pendingOnly, setPendingOnly] = useState(false);

  const [filterCampaigns, setFilterCampaigns] = useState([]);
  const [filterWorkers, setFilterWorkers] = useState([]);
  const listRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const loadDialogs = useCallback(async (page = 1, append = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", page);
      params.set("page_size", PAGE_SIZE);
      if (searchQuery) params.set("search", searchQuery);
      if (campaignFilter) params.set("campaign", campaignFilter);
      if (workerFilter) params.set("worker", workerFilter);
      if (repliesOnly) params.set("has_replies", "true");
      if (pendingOnly) params.set("has_pending", "true");

      const resp = await fetch(`${API_BASE}/dialogs/?${params}`, { headers: getHeaders() });
      if (resp.ok) {
        const data = await resp.json();
        const dialogs = data.dialogs || [];
        if (append) {
          setAllDialogs(prev => [...prev, ...dialogs]);
        } else {
          setAllDialogs(dialogs);
          setSelectedDialog(prev => {
            if (prev) {
              const still = dialogs.find(d => d.execution_id === prev.execution_id);
              return still || null;
            }
            return null;
          });
        }
        setTotalCount(data.pagination?.total || 0);
        setTotalPages(data.pagination?.total_pages || 1);
        setCurrentPage(page);
        if (data.filters) {
          setFilterCampaigns(data.filters.campaigns || []);
          setFilterWorkers(data.filters.workers || []);
        }
      }
    } catch (err) {
      add?.("Ошибка загрузки", "error");
    } finally {
      setLoading(false);
    }
  }, [searchQuery, campaignFilter, workerFilter, repliesOnly, pendingOnly]);

  useEffect(() => { loadDialogs(1); }, [loadDialogs]);

  const loadMore = () => {
    if (currentPage < totalPages && !loading) loadDialogs(currentPage + 1, true);
  };

  const handleScroll = useCallback((e) => {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 60 && currentPage < totalPages && !loading) {
      loadMore();
    }
  }, [currentPage, totalPages, loading]);

  const addIntentionToDialog = async (executionId, contactType, contactValue, text) => {
    try {
      const resp = await fetch(`${API_BASE}/reply-intentions/`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ execution_id: executionId, reply_text: text, contact_type: contactType, contact_value: contactValue }),
      });
      if (resp.ok) {
        const data = await resp.json();
        add?.("✅ Reply Intention создан", "success");
        setSelectedDialog(prev => {
          if (prev?.execution_id !== executionId) return prev;
          const newEvent = { event_type: "pending_reply", event_time: new Date().toISOString(), text, worker: prev.worker, intention_id: data.id, contact_type: contactType, contact_value: contactValue };
          return { ...prev, events: [...prev.events, newEvent], has_pending: true };
        });
        setAllDialogs(prev => prev.map(d => {
          if (d.execution_id === executionId) {
            const newEvent = { event_type: "pending_reply", event_time: new Date().toISOString(), text, worker: d.worker, intention_id: data.id, contact_type: contactType, contact_value: contactValue };
            return { ...d, events: [...d.events, newEvent], has_pending: true };
          }
          return d;
        }));
      } else {
        const err = await resp.json().catch(() => ({}));
        add?.(err.detail || "Ошибка создания интеншена", "error");
      }
    } catch (err) { add?.("Ошибка сети", "error"); }
  };

  const executeIntentions = async (executionId, intentionIds) => {
    try {
      const resp = await fetch(`${API_BASE}/reply-intentions/execute_bulk/`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ ids: intentionIds }),
      });
      if (resp.ok) {
        const data = await resp.json();
        const executedCount = data.executed?.length || 0;
        add?.(`✅ Выполнено: ${executedCount}`, "success");

        if (executedCount > 0) {
          setSelectedDialog(prev => {
            if (prev?.execution_id !== executionId) return prev;
            const executedTexts = [];
            const remainingEvents = prev.events.filter(ev => {
              if (ev.event_type === "pending_reply" && intentionIds.includes(ev.intention_id)) {
                executedTexts.push({ text: ev.text, worker: prev.worker });
                return false;
              }
              return true;
            });
            const newReplies = executedTexts.map(({ text, worker }) => ({
              event_type: "outgoing_reply",
              event_time: new Date().toISOString(),
              text,
              worker,
            }));
            const newEvents = [...remainingEvents, ...newReplies].sort((a, b) => a.event_time.localeCompare(b.event_time));
            const stillPending = newEvents.some(e => e.event_type === "pending_reply");
            return { ...prev, events: newEvents, has_pending: stillPending };
          });
          setAllDialogs(prev => prev.map(d => {
            if (d.execution_id === executionId) {
              const executedTexts = [];
              const remainingEvents = d.events.filter(ev => {
                if (ev.event_type === "pending_reply" && intentionIds.includes(ev.intention_id)) {
                  executedTexts.push({ text: ev.text, worker: d.worker });
                  return false;
                }
                return true;
              });
              const newReplies = executedTexts.map(({ text, worker }) => ({
                event_type: "outgoing_reply",
                event_time: new Date().toISOString(),
                text,
                worker,
              }));
              const newEvents = [...remainingEvents, ...newReplies].sort((a, b) => a.event_time.localeCompare(b.event_time));
              const stillPending = newEvents.some(e => e.event_type === "pending_reply");
              return { ...d, events: newEvents, has_pending: stillPending };
            }
            return d;
          }));
        }
      } else {
        const err = await resp.json().catch(() => ({}));
        add?.(err.detail || "Ошибка выполнения", "error");
      }
    } catch (err) { add?.("Ошибка сети", "error"); }
  };

  const showList = !isMobile || !selectedDialog;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 140px)" }}>
      {/* Тулбар */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e5e7eb", padding: "10px 16px", display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <input style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 12px", fontSize: 13, flex: "1 1 200px", minWidth: 150, outline: "none" }} placeholder="🔍 Поиск по username, email, url, имени, кампании, воркеру, execution ID..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onKeyDown={e => e.key === "Enter" && loadDialogs(1)} />
        <button onClick={() => loadDialogs(1)} style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 6, padding: "8px 16px", fontSize: 13, cursor: "pointer", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}><HiSearch size={14} /> Найти</button>
        <select value={campaignFilter} onChange={e => setCampaignFilter(e.target.value)} style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 12px", fontSize: 13, background: "#fff" }}><option value="">Все кампании</option>{filterCampaigns.map(c => <option key={c}>{c}</option>)}</select>
        <select value={workerFilter} onChange={e => setWorkerFilter(e.target.value)} style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 12px", fontSize: 13, background: "#fff", minWidth: 130 }}><option value="">Все воркеры</option>{filterWorkers.map(w => <option key={w}>{w}</option>)}</select>
        <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4, cursor: "pointer", whiteSpace: "nowrap" }}><input type="checkbox" checked={repliesOnly} onChange={e => setRepliesOnly(e.target.checked)} /> Ответы</label>
        <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4, cursor: "pointer", whiteSpace: "nowrap", color: "#d97706" }}><input type="checkbox" checked={pendingOnly} onChange={e => setPendingOnly(e.target.checked)} /> ⏳ Pending</label>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 12, color: "#9ca3af", whiteSpace: "nowrap" }}>{loading ? "..." : `${totalCount} диалогов`}</span>
      </div>

      {/* Основная область */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {showList && (
          <div style={{ width: isMobile ? "100%" : 380, minWidth: isMobile ? "100%" : 300, maxWidth: isMobile ? "100%" : 430, display: "flex", flexDirection: "column", background: "#fff", borderRight: isMobile ? "none" : "1px solid #e5e7eb" }}>
            <div ref={listRef} onScroll={handleScroll} style={{ flex: 1, overflow: "auto" }}>
              {allDialogs.length === 0 && !loading ? (
                <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>Нет диалогов</div>
              ) : (
                allDialogs.map((d, i) => <DialogCard key={d.execution_id || i} dialog={d} isSelected={selectedDialog?.execution_id === d.execution_id} onClick={() => setSelectedDialog(d)} />)
              )}
            </div>
            {currentPage < totalPages && !loading && <button onClick={loadMore} style={{ background: "#f9fafb", border: "none", borderTop: "1px solid #e5e7eb", color: "#6b7280", fontSize: 13, padding: "10px", cursor: "pointer" }}>Загрузить ещё ({totalCount - allDialogs.length} осталось)</button>}
            {loading && <div style={{ textAlign: "center", padding: 10, color: "#9ca3af", fontSize: 13 }}>Загрузка...</div>}
          </div>
        )}

        <DialogPanel dialog={selectedDialog} onClose={() => setSelectedDialog(null)} onAddIntention={addIntentionToDialog} onExecuteIntentions={executeIntentions} />
      </div>
    </div>
  );
}